<template>
  <b-modal
    v-model="editorShown"
    title="New Verification"
    size="md"
    centered
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    modal-class="new-verification-modal"
    header-class="bg-purple"
  >
    <template v-slot:modal-footer>
      <div class="text-right mt-1 mr-2">
        <b-button variant="secondary" @click="editorShown = false">
          Cancel
        </b-button>
        <b-button class="ml-2" variant="primary" @click="submitForm">
          Verify
        </b-button>
      </div>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="Location"
          :state="formErrors.location.length ? false : null"
          :invalid-feedback="formErrors.location.join('. ')"
        >
          <v-select
            :options="locationOptions"
            v-model="form.location"
            :disabled="episodeOfCareId != null"
          />
        </b-form-group>
        <b-form-group
          label="Insurance company"
          :state="formErrors.insurance.length ? false : null"
          :invalid-feedback="formErrors.insurance.join('. ')"
        >
          <v-select
            :class="
              insurances.length == 0
                ? 'insurance-company-picker-hide-options'
                : ''
            "
            placeholder="Search for insurance companies"
            v-model="form.insurance"
            :options="insurances"
            :disabled="episodeOfCareId != null"
            @search="fetchInsuranceCompanies"
          >
            <template #no-options="{ search }">
              {{
                search && search.length > 3 ? "Sorry, no matching options" : ""
              }}
            </template>
            <template #open-indicator>
              <span></span>
            </template>
          </v-select>
        </b-form-group>
        <b-form-group label="Service Code">
          <v-select v-model="form.serviceCode" :options="serviceCodeOptions" />
        </b-form-group>
        <b-form-group
          label="Service Dates"
          :state="formErrors.serviceDates.length ? false : null"
          :invalid-feedback="formErrors.serviceDates.join('. ')"
        >
          <div class="d-flex align-items-center">
            <date-picker
              v-model="form.serviceDateStart"
              :max="form.serviceDateEnd ? form.serviceDateEnd : null"
            />
            <span style="min-width:30px" class="mx-2">thru</span>
            <date-picker
              v-model="form.serviceDateEnd"
              :min="form.serviceDateStart ? form.serviceDateStart : null"
            />
          </div>
        </b-form-group>
        <b-form-group
          label="Subscriber First Name"
          :state="formErrors.firstName.length ? false : null"
          :invalid-feedback="formErrors.firstName.join('. ')"
        >
          <b-form-input
            v-model="form.firstName"
            trim
            autocomplete="off"
            maxLength="100"
          />
        </b-form-group>
        <b-form-group
          label="Subscriber Last Name"
          :state="formErrors.lastName.length ? false : null"
          :invalid-feedback="formErrors.lastName.join('. ')"
        >
          <b-form-input
            v-model="form.lastName"
            trim
            autocomplete="off"
            maxLength="100"
          />
        </b-form-group>
        <b-row>
          <b-col>
            <b-form-group label="DOB">
              <date-picker v-model="form.dateOfBirth" />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="Member ID"
              :state="formErrors.memberId.length ? false : null"
              :invalid-feedback="formErrors.memberId.join('. ')"
            >
              <b-form-input
                v-model="form.memberId"
                trim
                autocomplete="off"
                maxLength="100"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group
          class="member-type"
          label="Member Type"
          :state="formErrors.memberType.length ? false : null"
          :invalid-feedback="formErrors.memberType.join('. ')"
        >
          <b-form-radio-group
            v-model="form.memberType"
            :options="memberTypes"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DatePicker from "../controls/DatePicker";
import eligibilityVerificationServer from "../../server/eligibilityVerification";
import eligibilityServiceCodeServer from "../../server/eligibilityServiceCode";
import eocServer from "../../server/eoc";
import locationServer from "../../server/location";
import insuranceCompanyServer from "../../server/insuranceCompany";
import { formatDateOnlyMysql } from "../../helpers/date";
import { validateRequired } from "../../helpers/validation";

export default {
  name: "AddEditEligibilityModal",
  components: { DatePicker },
  props: ["eligibilityVerificationId", "episodeOfCareId", "patientInsuranceId"],
  data() {
    return {
      form: {
        location: null,
        insurance: null,
        serviceCode: null,
        serviceDateStart: null,
        serviceDateEnd: null,
        firstName: null,
        lastName: null,
        dateOfBirth: null,
        memberId: null,
        memberType: null
      },
      formErrors: {
        location: [],
        insurance: [],
        serviceCode: [],
        serviceDates: [],
        firstName: [],
        lastName: [],
        memberId: [],
        memberType: []
      },
      locations: [],
      insurances: [],
      serviceCodes: [],
      insurancePickerDisabled: false,
      memberTypes: [
        { value: "Subscriber", text: "Subscriber" },
        { value: "Dependent", text: "Dependent" }
      ]
    };
  },
  async mounted() {
    this.locations = await locationServer.query();
    this.serviceCodes = await eligibilityServiceCodeServer.query();

    const dateTime = formatDateOnlyMysql(this.getCurrentAdjustedDateTime());

    if (this.eligibilityVerificationId && !this.episodeOfCareId) {
      const verification = await eligibilityVerificationServer.get(
        this.eligibilityVerificationId
      );
      this.form = {
        location: this.locationOptions.find(
          u => u.value == verification.location.id
        ),
        insurance: {
          value: verification.insurance.id,
          label: verification.insurance.name
        },
        serviceCode: this.serviceCodeOptions.find(
          u => u.value == verification.serviceCode.id
        ),
        serviceDateStart: dateTime,
        serviceDateEnd: dateTime,
        firstName: verification.memberFirstName,
        lastName: verification.memberLastName,
        dateOfBirth: verification.memberDateOfBirth
          ? formatDateOnlyMysql(verification.memberDateOfBirth)
          : null,
        memberId: verification.memberId,
        memberType: verification.memberType
      };
    } else if (this.episodeOfCareId && this.patientInsuranceId) {
      const eoc = await eocServer.get(this.episodeOfCareId);
      const insurance = eoc.patient.insurances.find(
        u => u.id == this.patientInsuranceId
      );
      this.form = {
        location: this.locationOptions.find(u => u.value == eoc.location.id),
        insurance: {
          value: insurance.company.id,
          label: insurance.company.name
        },
        serviceCode: this.serviceCodeOptions.find(u => u.data.code == "30"),
        serviceDateStart: dateTime,
        serviceDateEnd: dateTime,
        firstName: eoc.patient.firstName,
        lastName: eoc.patient.lastName,
        dateOfBirth: eoc.patient.dateOfBirth
          ? formatDateOnlyMysql(eoc.patient.dateOfBirth)
          : null,
        memberId: insurance.memberIdent,
        memberType: null
      };
    } else {
      this.form = {
        ...this.form,
        serviceCode: this.serviceCodeOptions.find(u => u.data.code == "30"),
        serviceDateStart: dateTime,
        serviceDateEnd: dateTime
      };
    }
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      const verification = {
        locationId: this.form.location.value,
        insuranceId: this.form.insurance.value,
        serviceCodeId: this.form.serviceCode.value,
        serviceDateStart: this.form.serviceDateStart,
        serviceDateEnd: this.form.serviceDateEnd,
        memberFirstName: this.form.firstName,
        memberLastName: this.form.lastName,
        memberDateOfBirth: this.form.dateOfBirth,
        memberId: this.form.memberId,
        memberType: this.memberType,
        episodeOfCareId: this.episodeOfCareId
      };

      const result = await eligibilityVerificationServer.create(verification);

      this.showAlertSuccess();
      this.$emit("onModify", {
        showResultModal: true,
        verificationId: result.id
      });
      this.editorShown = false;
    },
    validateForm() {
      let withoutErrors = true;

      this.formErrors.location = [];
      if (!validateRequired(this.form.location)) {
        this.formErrors.location.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.insurance = [];
      if (!validateRequired(this.form.insurance)) {
        this.formErrors.insurance.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.serviceCode = [];
      if (!validateRequired(this.form.serviceCode)) {
        this.formErrors.serviceCode.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.serviceDates = [];
      if (!validateRequired(this.form.serviceDateStart)) {
        this.formErrors.serviceDates.push("This field is required");
        withoutErrors = false;
      } else if (!validateRequired(this.form.serviceDateEnd)) {
        this.formErrors.serviceDates.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.firstName = [];
      if (!validateRequired(this.form.firstName)) {
        this.formErrors.firstName.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.lastName = [];
      if (!validateRequired(this.form.lastName)) {
        this.formErrors.lastName.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.memberId = [];
      if (!validateRequired(this.form.memberId)) {
        this.formErrors.memberId.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.memberType = [];
      if (!validateRequired(this.form.memberType)) {
        this.formErrors.memberType.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async fetchInsuranceCompanies(search, loading) {
      if (this.lastTimeoutInsurance) {
        clearTimeout(this.lastTimeoutInsurance);
        this.lastTimeoutInsurance = null;
      }
      if (search && search.trim().length >= 3) {
        this.insurances = [];
      }

      this.lastTimeoutInsurance = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const insurances = await insuranceCompanyServer.query(
            { searchPhrase: search, page: 0, pageSize: 1000000 },
            { "Hide-Loading": true }
          );
          this.insurances = insurances.records.map(insuranceCompany => ({
            value: insuranceCompany.id,
            label: insuranceCompany.name,
            data: insuranceCompany
          }));
        } else this.insurances = [];
        loading(false);
      }, 1000);
    }
  },
  computed: {
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    locationOptions() {
      return this.locations.map(loc => ({
        value: loc.id,
        label: loc.name
      }));
    },
    serviceCodeOptions() {
      return this.serviceCodes.map(c => ({
        value: c.id,
        label: `${c.name} (${c.code})`,
        data: c
      }));
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.new-verification-modal {
  .v-select.vs--disabled .vs__dropdown-toggle {
    background-color: #f8f8f8 !important;
  }

  .insurance-company-picker-hide-options {
    .vs__dropdown-menu {
      display: none;
    }

    .vs--open .vs__dropdown-toggle {
      border-bottom-color: #4044e6 !important;
      border-bottom-width: 2px;
    }
  }
}
</style>
<style scoped>
.member-type >>> .custom-radio {
  margin-right: 20px !important;
}
</style>
